import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquare, faSquareCheck } from "@fortawesome/free-regular-svg-icons";
import { useDocumentTitle } from "$utils/document-title";
import { formatDate } from "$utils/date-helpers";
import api from "$services/api";
import featureFlagsApi from "./api";

const MAINTENANCE_MODE_FLAG_NAME = "MaintenanceMode.On";

const FeatureFlagsAdmin = () => {
    useDocumentTitle("Feature Flags - Admin");

    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        featureFlagsApi
            .getFlags(controller.signal)
            .then((response) => {
                setFlags(response || []);
                setError(false);
            })
            .catch((e) => {
                if (!api.requestWasCanceled(e)) {
                    setError(true);
                }
            })
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, []);

    const applyToggle = async (flag, nextEnabled) => {
        setFlags((current) => current.map((f) => (f.id === flag.id ? { ...f, enabled: nextEnabled } : f)));
        try {
            const updated = await featureFlagsApi.toggleFlag(flag.id, nextEnabled);
            if (updated) {
                setFlags((current) => current.map((f) => (f.id === updated.id ? updated : f)));
            }
        } catch (e) {
            if (!api.requestWasCanceled(e)) {
                toast("Failed to update feature flag. Please try again.");
                setFlags((current) => current.map((f) => (f.id === flag.id ? { ...f, enabled: !nextEnabled } : f)));
            }
        }
    };

    const handleToggle = (flag) => {
        const nextEnabled = !flag.enabled;
        if (flag.name === MAINTENANCE_MODE_FLAG_NAME) {
            const verb = nextEnabled ? "enable" : "disable";
            if (!window.confirm(`Are you sure you want to ${verb} Maintenance Mode? This takes the entire app offline for all users.`)) {
                return;
            }
        }
        applyToggle(flag, nextEnabled);
    };

    const categories = flags.reduce((acc, flag) => {
        acc[flag.category] = acc[flag.category] || [];
        acc[flag.category].push(flag);
        return acc;
    }, {});

    if (loading) return <p className="txt -muted">Loading feature flags...</p>;
    if (error) return <p className="txt -muted">Unable to load feature flags. Please refresh the page.</p>;
    if (flags.length === 0) return <p className="txt -muted">No feature flags found.</p>;

    return (
        <div>
            <h1>Feature Flags</h1>
            {Object.keys(categories).map((category) => (
                <section key={category} style={{ marginBottom: "24px" }}>
                    <h2>{category}</h2>
                    <ul style={{ listStyle: "none", padding: 0 }}>
                        {categories[category].map((flag) => (
                            <li
                                key={flag.id}
                                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #eee" }}
                            >
                                <div className="form-field -checkbox">
                                    <div className={`check ${flag.enabled ? "-selected" : ""}`}>
                                        <input type="checkbox" checked={flag.enabled} onChange={() => handleToggle(flag)} />
                                        <FontAwesomeIcon icon={flag.enabled ? faSquareCheck : faSquare} />
                                    </div>
                                    <label>{flag.label}</label>
                                </div>
                                <span className="txt -muted" style={{ fontSize: "0.875rem" }}>
                                    {flag.lastModifiedBy
                                        ? `Last changed by ${flag.lastModifiedBy} on ${formatDate(flag.lastModifiedAt)}`
                                        : "Never changed via this UI"}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
};

export default FeatureFlagsAdmin;
